import React, { useContext, useEffect, useState } from 'react'
import API from '../../../../services/API'
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Slide,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material'
import { useParams } from 'react-router-dom'
import Comments from './Comments'
import Details from './Details'
import WitnessStatements from './WitnessStatements'
import Investigation from './Investigation'
import CorrectiveActions from './CorrectiveActions'
import IncidentInfo from './IncidentInfo'
import IncidentHero from './IncidentHero'
import Save from '@mui/icons-material/Save'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import Loading from '../../../loading/loadingPage'
import AccessDenied from '../../../../components/AccessDenied'
import BigModal from '../../../../components/Modals/BigModal'
import { PDFViewer } from '@react-pdf/renderer'
import IncidentPDF from './IncidentPDF'
import LogsComponent from '../../../../components/Logs/LogsComponent'
import { GlobalContext, TenantSettingsContext, UserContext } from '../../../../App'

const TabLabel = ({ label, count, color }) => (
  <Stack direction="row" spacing={0.75} alignItems="center">
    <span>{label}</span>
    {count !== undefined && count > 0 && (
      <Chip
        size="small"
        label={count}
        color={color}
        sx={{ borderRadius: 1, height: 18, fontSize: '0.7rem' }}
      />
    )}
  </Stack>
)

// Only these fields are sent on save — they mirror the server's editable-field
// whitelist, and every change lands in the incident's audit trail (History
// tab). Attachments and witness statements save through their own $push calls.
const buildSavePayload = (incident) => ({
  incidentDate: incident.incidentDate,
  incidentLocation: incident.incidentLocation,
  incidentType: incident.incidentType,
  description: incident.description,
  initialActions: incident.initialActions,
  relatedAsset: incident.relatedAsset,
  peopleInvolved: incident.peopleInvolved ?? [],
  responsibleManager: incident.responsibleManager ?? null,
  hiddenFrom: incident.hiddenFrom ?? [],
  status: incident.status,
  result: incident.result,
  actualImpact: incident.actualImpact,
  whatHappened: incident.whatHappened,
  whyItHappened: incident.whyItHappened,
  howItHappened: incident.howItHappened,
  whatActions: incident.whatActions,
  concernsIdentified: incident.concernsIdentified,
  mitigationsPositives: incident.mitigationsPositives,
  investigationProcess: incident.investigationProcess,
  rootCauses: incident.rootCauses,
  systemicIssues: incident.systemicIssues,
  dutyOfCandour: incident.dutyOfCandour,
  assessedImpact: incident.assessedImpact,
  lessonsLearned: incident.lessonsLearned,
  riskRegisterAdditions: incident.riskRegisterAdditions,
  recommendations: incident.recommendations,
  actionsRequired: incident.actionsRequired,
  noCorrectiveActionsRequired: !!incident.noCorrectiveActionsRequired,
  noCorrectiveActionsReason: incident.noCorrectiveActionsRequired
    ? incident.noCorrectiveActionsReason
    : '',
  clinical: incident.clinical,
  isSafeguarding: incident.isSafeguarding,
  controlledDrugs: incident.controlledDrugs,
  manHandling: incident.manHandling,
  ipc: incident.ipc,
  sharps: incident.sharps,
  cprUsed: incident.cprUsed,
})

const Incident = () => {
  const [incident, setIncident] = useState({})
  const { id: incidentID } = useParams()
  const [selectedTab, setSelectedTab] = useState('overview')
  const [changesPresent, setChangesPresent] = useState(false)
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(true)
  const [accessDenied, setAccessDenied] = useState(false)
  const [openPDF, setOpenPDF] = useState(false)
  const { user } = useContext(UserContext)
  const { setToast } = useContext(GlobalContext)

  const { orgSettings } = useContext(TenantSettingsContext).orgConfig

  const loadIncident = () => {
    setLoading(true)
    API.get('/incident?incidentID=' + incidentID)
      .then((res) => {
        if (res.status === 400 || res.status === 403) {
          setAccessDenied(true)
        } else {
          setIncident(res.data)
        }
      })
      .catch((e) => console.log(e))
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    loadIncident()
  }, [])

  const handleChange = (fieldName, value) => {
    setIncident((prev) => ({ ...prev, [fieldName]: value }))
    setChangesPresent(true)
  }

  const submitChanges = () => {
    setSaving(true)
    API.put('/incident/' + incident._id, buildSavePayload(incident))
      .then((res) => {
        if (res.status === 200) {
          setChangesPresent(false)
          setToast({ message: 'Incident updated — changes logged.' })
        } else {
          setToast({
            message: 'Unable to save changes. Please try again.',
          })
        }
      })
      .finally(() => setSaving(false))
  }

  const tabs = [
    { key: 'overview', label: 'Overview' },
    { key: 'involved', label: 'People & Involvement' },
    { key: 'investigation', label: 'Investigation' },
    { key: 'corrective', label: 'Corrective Actions' },
    {
      key: 'witnesses',
      label: 'Witness Statements',
      count: incident?.witnessStatements?.length,
      color: 'info',
    },
    {
      key: 'notes',
      label: 'Notes',
      count: incident?.comments?.length,
      color: 'default',
    },
    { key: 'history', label: 'History' },
  ]

  return (
    <Box>
      {loading ? (
        <Loading />
      ) : (
        <AccessDenied denied={accessDenied}>
          <Stack spacing={2}>
            <IncidentHero
              incident={incident}
              handlePDFDownload={() => setOpenPDF(true)}
            />
            <BigModal
              open={!!openPDF}
              onClose={() => setOpenPDF(false)}
              style={{ height: 600 }}
            >
              <Box minHeight="80vh" flexGrow={1} flex={1} height="100%">
                <PDFViewer width="100%" height="100%">
                  <IncidentPDF
                    incident={incident}
                    printedBy={`${user.firstName} ${user.lastName}`}
                    orgSettings={orgSettings}
                  />
                </PDFViewer>
              </Box>
            </BigModal>
            <Paper sx={{ borderRadius: 3, overflow: 'hidden' }}>
              <Tabs
                value={selectedTab}
                onChange={(event, newValue) => setSelectedTab(newValue)}
                variant="scrollable"
                allowScrollButtonsMobile
                sx={{
                  px: 1,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 },
                }}
              >
                {tabs.map((tabDef) => (
                  <Tab
                    key={tabDef.key}
                    value={tabDef.key}
                    label={
                      <TabLabel
                        label={tabDef.label}
                        count={tabDef.count}
                        color={tabDef.color}
                      />
                    }
                  />
                ))}
              </Tabs>
              <Box p={2}>
                {selectedTab === 'overview' && (
                  <Details incident={incident} handleChange={handleChange} />
                )}
                {selectedTab === 'involved' && (
                  <IncidentInfo incident={incident} handleChange={handleChange} />
                )}
                {selectedTab === 'investigation' && (
                  <Investigation
                    incident={incident}
                    setIncident={setIncident}
                    setChangesPresent={setChangesPresent}
                    handleChange={handleChange}
                  />
                )}
                {selectedTab === 'corrective' && (
                  <CorrectiveActions
                    incident={incident}
                    handleChange={handleChange}
                  />
                )}
                {selectedTab === 'witnesses' && (
                  <WitnessStatements
                    incident={incident}
                    setIncident={setIncident}
                    loadIncident={loadIncident}
                  />
                )}
                {selectedTab === 'notes' && (
                  <Comments
                    incident={incident}
                    loadIncident={loadIncident}
                    setIncident={setIncident}
                  />
                )}
                {selectedTab === 'history' && (
                  <Stack spacing={1}>
                    <Alert severity="info" sx={{ borderRadius: 2 }}>
                      Every edit to this incident is recorded here — who changed
                      what, from what, to what. Audit entries cannot be edited
                      or removed.
                    </Alert>
                    <LogsComponent
                      targetType="incident"
                      targetId={incident._id}
                      title="Audit Entrie"
                      hideTitle
                      disableFiltering={false}
                      overflow="auto"
                    />
                  </Stack>
                )}
              </Box>
            </Paper>
          </Stack>
        </AccessDenied>
      )}
      {changesPresent && (
        <Slide direction="up" in={changesPresent} mountOnEnter unmountOnExit>
          <Paper
            elevation={8}
            sx={{
              position: 'sticky',
              bottom: 12,
              mt: 2,
              px: 2,
              py: 1.5,
              borderRadius: 3,
              zIndex: 100,
              display: 'flex',
              flexDirection: { xs: 'column', lg: 'row' },
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: 1,
              border: '1px solid',
              borderColor: 'warning.main',
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip
                size="small"
                color="warning"
                label="Unsaved changes"
                sx={{ borderRadius: 1, fontWeight: 700 }}
              />
              <Typography variant="body2" color="text.secondary">
                Saved changes are recorded in the incident's audit history.
              </Typography>
            </Stack>
            <Stack direction="row" gap={1}>
              <Button
                onClick={() => {
                  loadIncident()
                  setChangesPresent(false)
                }}
                variant="outlined"
                color="inherit"
                startIcon={<RestartAltIcon />}
              >
                Reset
              </Button>
              <Button
                onClick={submitChanges}
                variant="contained"
                color="success"
                disabled={saving}
                startIcon={<Save />}
              >
                {saving ? 'Saving…' : 'Save Changes'}
              </Button>
            </Stack>
          </Paper>
        </Slide>
      )}
    </Box>
  )
}

export default Incident
